import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Article, CreateArticle, UpdateArticle } from '@thro-types/leihbar';

import { AxiosRequestConfig } from 'axios';

import {
  createLeihbarArticle,
  deleteLeihbarArticle,
  getArticlesForProduct,
  getArticlesWithConflictBookings,
  getAvailableArticles,
  getVisibleArticlesForProduct,
  updateLeihbarArticle
} from 'api/leihbar';
import { AvailableArticleConfig } from 'api/types/leihbar';

export const useAvailableArticleQuery = (
  enabled = true,
  productId: number,
  config?: AvailableArticleConfig
) => {
  const query = useQuery({
    queryKey: ['articles', productId, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const articles = getAvailableArticles(productId, config);
      return articles;
    },
    enabled,
    initialData: []
  });

  return query;
};

export const useArticlesWithConflictBookingsQuery = (
  enabled = true,
  productId: number,
  config?: AvailableArticleConfig
) => {
  const query = useQuery({
    queryKey: ['articles', productId, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const articles = getArticlesWithConflictBookings(productId, config);
      return articles;
    },
    enabled
  });

  return query;
};

export const useVisibleArticlesForProductQuery = (
  productId: number,
  config?: AxiosRequestConfig
) => {
  const query = useQuery({
    queryKey: ['articles', productId, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const articles = getVisibleArticlesForProduct(productId, config);
      return articles;
    }
  });

  return query;
};

export const useLeihbarArticlesForProductQuery = (
  productId: number,
  config?: AxiosRequestConfig
) => {
  const query = useQuery({
    queryKey: ['articles', productId, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const articles = getArticlesForProduct(productId, config);
      return articles;
    }
  });

  return query;
};

export const useLeihbarArticleMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['articles'];

  const create = useMutation<Article, Error, CreateArticle>({
    mutationFn: async args => {
      return await createLeihbarArticle(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<Article, Error, { id: number; data: UpdateArticle }>({
    mutationFn: async args => {
      return await updateLeihbarArticle(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<Article, Error, { id: number; productId: number }>({
    mutationFn: async args => {
      return await deleteLeihbarArticle(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
